import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiMoreHorizontal } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useFloatingPosition from '../hooks/useFloatingPosition';

function ActionMenu({ label, items }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const style = useFloatingPosition({ open, anchorRef: buttonRef, floatRef: listRef, onDismiss: close });

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (!buttonRef.current?.contains(event.target) && !listRef.current?.contains(event.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', onDown);
    listRef.current?.querySelector('[role="menuitem"]')?.focus({ preventScroll: true });
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  const onKeyDown = (event) => {
    const nodes = Array.from(listRef.current.querySelectorAll('[role="menuitem"]'));
    const current = nodes.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      buttonRef.current?.focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      nodes[(current + 1) % nodes.length].focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      nodes[(current - 1 + nodes.length) % nodes.length].focus();
    } else if (event.key === 'Tab') {
      close();
    }
  };

  return (
    <div className="menu">
      <button
        type="button"
        ref={buttonRef}
        className="icon-btn"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <SafeIcon icon={FiMoreHorizontal} size={18} />
      </button>
      {open &&
        createPortal(
          <div className="menu-list" role="menu" ref={listRef} style={style} onKeyDown={onKeyDown}>
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                className={`menu-item${item.danger ? ' menu-item-danger' : ''}`}
                onClick={() => {
                  close();
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

export default ActionMenu;